// DEMO ONLY. The Growth Planner hand-off (Figma 6473:11977, 12314, 12607, 12872, 13248) as page
// response blocks: the payloads a live answer would carry.
import {
  GrowthPlannerHandoffBlock,
  GrowthPlannerHandoffResult,
  GrowthPlannerHandoffStatus,
  GrowthPlannerMetricOption,
  SupportAction
} from '../../support/types';
import { CHART_QUESTION, GOVERNED_ANALYSIS_SEED } from './answerSeeds';
import { IOrbitPageDemoChat, seedTurn } from './seedTurn';

const CHIPS = ['Screen: Customer Insights', 'Region: All', 'Period: Last 30 Days'];

const meta = (confidence: string) => [
  '12,420 Total customers',
  '10000 Eligible Customer',
  `Confidence ${confidence}`
];

// 6473:12805-12836, row by row: the cards fill two columns left to right.
const METRICS: GrowthPlannerMetricOption[] = [
  {
    id: 'credit_card_readiness',
    title: 'Credit Card Readiness',
    meta: meta('92%'),
    selected: true,
    available: true
  },
  {
    id: 'savings_product_readiness',
    title: 'Savings Product Readiness',
    meta: meta('92%'),
    selected: false,
    available: true
  },
  {
    id: 'personal_loan_readiness',
    title: 'Personal Loan Readiness',
    meta: meta('92%'),
    selected: false,
    available: true
  },
  {
    id: 'mortgage_readiness',
    title: 'Mortgage Readiness',
    meta: meta('62%'),
    selected: false,
    available: false,
    unavailable_reason: 'Unavailable: below the tenant confidence threshold of 70%.'
  }
];

const CONTINUE: SupportAction = {
  id: 'continue_to_growth_planner',
  label: 'Continue to Growth Planner →',
  kind: 'primary'
};

const RESULTS: Partial<Record<GrowthPlannerHandoffStatus, GrowthPlannerHandoffResult>> = {
  sending: {
    title: 'Sending to Growth Planner…',
    // 6473:13242 breaks after the comma.
    message: 'Preparing handoff package for 1 metric ,\n12,420 customers.',
    highlights: ['1 metric', '12,420 customers.'],
    progress: 40
  },
  sent: {
    title: 'Customer Value Trend signal\nis successfully sent to Growth Planner',
    actions: [
      {
        id: 'view_in_growth_planner',
        label: 'View in Growth Planner ↗',
        kind: 'primary',
        tone: 'accent'
      }
    ]
  },
  failed: {
    title: "Couldn't send to Growth Planner",
    actions: [{ id: 'retry_handoff', label: 'Retry', kind: 'primary', tone: 'error' }]
  }
};

const handoff = (status: GrowthPlannerHandoffStatus): GrowthPlannerHandoffBlock => {
  const chooser = status !== 'preparing';
  return {
    type: 'growth_planner_handoff',
    id: `handoff-${status}`,
    status,
    status_line: 'Orbit is analyzing...',
    metrics: chooser ? { heading: '1 · Choose actionable metrics', options: METRICS } : null,
    checks: chooser
      ? {
          heading: '2 · Handoff checks',
          rows: [
            {
              id: 'subscription',
              label: 'Growth Planner subscription',
              value: 'Passed',
              tone: 'success'
            },
            { id: 'permission', label: 'Permission to hand off', value: 'Passed', tone: 'success' },
            { id: 'consent', label: 'Consent and suppression', value: 'Passed', tone: 'success' },
            { id: 'cohort', label: 'Minimum cohort size', value: 'Passed', tone: 'success' },
            { id: 'evidence', label: 'Evidence attached', value: '3 drivers', tone: 'warning' }
          ]
        }
      : null,
    summary: chooser
      ? {
          items: ['1 metric selected', '10,000 eligible customers'],
          note: {
            tone: 'warning',
            text: 'All selected actionable metrics in this card will be handed off together.'
          }
        }
      : null,
    // 6473:12859 / 13124 / 13500: Continue stays greyed once the hand-off has started.
    actions: chooser ? [{ ...CONTINUE, disabled: status !== 'choosing' }] : [],
    result: RESULTS[status] ?? null
  };
};

/**
 * The per-type seeds by name. `showcaseSeed.ts` composes the Pinned test chat from them, so an
 * edit here shows there too: keep the names.
 */
export const HANDOFF_SEEDS = { handoff };

const handoffChat = (
  id: string,
  title: string,
  status: GrowthPlannerHandoffStatus
): IOrbitPageDemoChat => ({
  id,
  title,
  chips: CHIPS,
  turns: [seedTurn(CHART_QUESTION, [GOVERNED_ANALYSIS_SEED]), seedTurn('', [handoff(status)])]
});

export const HANDOFF_DEMO_CHATS: IOrbitPageDemoChat[] = [
  handoffChat('gp-preparing', 'Growth Planner: preparing', 'preparing'),
  handoffChat('gp-handoff', 'Growth Planner: choose metrics', 'choosing'),
  handoffChat('gp-sending', 'Growth Planner: sending', 'sending'),
  handoffChat('gp-sent', 'Growth Planner: sent', 'sent'),
  handoffChat('gp-failed', 'Growth Planner: failed', 'failed')
];
